/**
 * WSH DAW-Ready Sessions in Action — Video section JS
 * Handles: pill click → video swap, pager dot click → kit swap,
 * caption updates (key expansion, BPM, meter), IntersectionObserver → preload upgrade.
 * Idempotent: safe to call init() multiple times (re-render resilient).
 */
(function () {
  'use strict';

  var SECTION_SEL = '[data-wsh-section="daw-ready-sessions-in-action"]';

  // Media coordinator integration (matches Brief 5 event contract).
  // Bound once at module scope (survives section re-render) — pauses all videos
  // in this section when another media surface takes control.
  function pauseAllInActionVideos() {
    var section = document.querySelector(SECTION_SEL);
    if (!section) return;
    var videos = section.querySelectorAll('video');
    for (var i = 0; i < videos.length; i++) {
      if (!videos[i].paused) videos[i].pause();
    }
    var activeWrap = section.querySelector('.wsh-daw-action__video-wrap.is-playing');
    if (activeWrap) activeWrap.classList.remove('is-playing');
  }

  document.addEventListener('videos:pause', pauseAllInActionVideos);
  document.addEventListener('wsh:video:play', function (e) {
    if (e && e.detail && e.detail.source !== 'daw-ready-sessions-in-action') {
      pauseAllInActionVideos();
    }
  });

  /**
   * Expand a key token to readable form.
   * Trailing 'm' = minor; 's' after note = sharp; 'b' after note = flat.
   * Examples: D → "D major", Dm → "D minor", Fs → "F-sharp major", Bbm → "B-flat minor"
   */
  function expandKey(token) {
    if (!token) return '';
    var note = token.charAt(0).toUpperCase();
    var rest = token.substring(1);
    var mode = 'major';

    if (rest.length > 0 && rest.charAt(rest.length - 1) === 'm') {
      mode = 'minor';
      rest = rest.substring(0, rest.length - 1);
    }

    var accidental = '';
    if (rest === 's') accidental = '-sharp';
    else if (rest === 'b') accidental = '-flat';

    return note + accidental + ' ' + mode;
  }

  /**
   * Build caption string from a video wrap's data attributes.
   * Returns empty string if all fields are empty (legacy entry).
   */
  function buildCaption(wrap) {
    var parts = [];
    var kitNum = wrap.dataset.kitNumber;
    var bpm = wrap.dataset.bpm;
    var key = wrap.dataset.key;
    var meter = wrap.dataset.meter;

    if (kitNum) {
      var n = parseInt(kitNum, 10);
      if (!isNaN(n)) {
        parts.push('Song Kit ' + (n < 10 ? '0' + n : '' + n));
      }
    }
    if (bpm) parts.push(bpm + ' BPM');
    if (key) {
      var expanded = expandKey(key);
      if (expanded) parts.push(expanded);
    }
    if (meter) parts.push(meter.replace(/_/g, '/') + ' time');

    return parts.join(' \u00B7 ');
  }

  /**
   * Get video wraps for a DAW, sorted by kit_number ascending (nulls last).
   */
  function getWrapsForDaw(section, dawHandle) {
    var wraps = section.querySelectorAll('.wsh-daw-action__video-wrap[data-daw="' + dawHandle + '"]');
    var arr = Array.prototype.slice.call(wraps);
    arr.sort(function (a, b) {
      var aKit = a.dataset.kitNumber ? parseInt(a.dataset.kitNumber, 10) : 999;
      var bKit = b.dataset.kitNumber ? parseInt(b.dataset.kitNumber, 10) : 999;
      return aKit - bKit;
    });
    return arr;
  }

  function init() {
    var section = document.querySelector(SECTION_SEL);
    if (!section) return;

    if (section.dataset.wshInited === 'true') return;
    section.dataset.wshInited = 'true';

    var pills = section.querySelectorAll('.wsh-daw-action__pill');
    var videoWraps = section.querySelectorAll('.wsh-daw-action__video-wrap');
    var captionEl = section.querySelector('[data-role="caption"]');
    var pagerEl = section.querySelector('[data-role="pager"]');

    if (!pills.length || !videoWraps.length) return;

    function pauseAllVideos() {
      var videos = section.querySelectorAll('video');
      for (var i = 0; i < videos.length; i++) {
        if (!videos[i].paused) videos[i].pause();
      }
    }

    function hideAllWraps() {
      for (var i = 0; i < videoWraps.length; i++) {
        videoWraps[i].dataset.active = 'false';
        videoWraps[i].style.display = 'none';
        videoWraps[i].classList.remove('wsh-daw-action__video-wrap--active');
        videoWraps[i].classList.remove('is-playing');
      }
    }

    function showWrap(wrap) {
      wrap.dataset.active = 'true';
      wrap.style.display = '';
      wrap.classList.add('wsh-daw-action__video-wrap--active');

      if (section.dataset.wshInView === 'true') {
        var video = wrap.querySelector('video');
        if (video && video.preload === 'none') {
          video.preload = 'metadata';
        }
      }
    }

    function updateCaption(wrap) {
      if (!captionEl) return;
      var text = buildCaption(wrap);
      if (text) {
        captionEl.textContent = text;
        captionEl.style.display = '';
        section.classList.add('has-meta');
      } else {
        captionEl.textContent = '';
        captionEl.style.display = 'none';
        section.classList.remove('has-meta');
      }
    }

    function renderPager(dawHandle) {
      if (!pagerEl) return;
      var wraps = getWrapsForDaw(section, dawHandle);

      if (wraps.length <= 1) {
        pagerEl.innerHTML = '';
        pagerEl.style.display = 'none';
        return;
      }

      pagerEl.style.display = '';
      var html = '';
      for (var i = 0; i < wraps.length; i++) {
        var isActive = wraps[i].dataset.active === 'true';
        html += '<button class="wsh-daw-action__dot' + (isActive ? ' wsh-daw-action__dot--active' : '') + '" data-daw="' + dawHandle + '" data-position="' + i + '" aria-label="Kit ' + (i + 1) + '">';
        if (isActive) {
          html += '<span class="wsh-daw-action__dot-counter">' + (i + 1) + ' of ' + wraps.length + '</span>';
        }
        html += '</button>';
      }
      pagerEl.innerHTML = html;

      var dots = pagerEl.querySelectorAll('.wsh-daw-action__dot');
      for (var d = 0; d < dots.length; d++) {
        dots[d].addEventListener('click', onPagerClick);
      }
    }

    function switchToPosition(dawHandle, position) {
      pauseAllVideos();
      hideAllWraps();
      var wraps = getWrapsForDaw(section, dawHandle);
      if (position < wraps.length) {
        showWrap(wraps[position]);
        updateCaption(wraps[position]);
      }
      renderPager(dawHandle);
    }

    function switchDaw(dawHandle) {
      for (var p = 0; p < pills.length; p++) {
        var isActive = pills[p].dataset.daw === dawHandle;
        pills[p].classList.toggle('wsh-daw-action__pill--active', isActive);
        pills[p].setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
      switchToPosition(dawHandle, 0);
    }

    function onPagerClick() {
      var dawHandle = this.dataset.daw;
      var position = parseInt(this.dataset.position, 10);
      switchToPosition(dawHandle, position);
    }

    // Pill click handler
    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        switchDaw(pill.dataset.daw);
      });
    });

    // --- Custom video player (no native controls) ---
    var allVideos = section.querySelectorAll('video');

    allVideos.forEach(function (video) {
      var wrap = video.closest('.wsh-daw-action__video-wrap');
      if (!wrap) return;

      video.removeAttribute('controls');

      wrap.addEventListener('click', function () {
        if (video.paused) {
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', function () {
        wrap.classList.add('is-playing');
        // Media coordinator: signal other surfaces to yield.
        document.dispatchEvent(new CustomEvent('wsh:video:play', {
          detail: { source: 'daw-ready-sessions-in-action' }
        }));
        // Belt-and-braces: also pause audio manager directly.
        var mgr = document.querySelector('wsh-audio-manager');
        if (mgr && typeof mgr.pause === 'function') mgr.pause();
      });
      video.addEventListener('pause', function () {
        wrap.classList.remove('is-playing');
      });
      video.addEventListener('ended', function () {
        wrap.classList.remove('is-playing');
      });
    });

    // Auto-advance on video end — bound directly to each <video> element
    // because the ended event does not bubble (delegation silently fails).
    // Matches the pattern in wsh-daw-collection-sessions-video.js.
    allVideos.forEach(function (video) {
      video.addEventListener('ended', function () {
        var wrap = video.closest('.wsh-daw-action__video-wrap');
        if (!wrap || wrap.dataset.active !== 'true') return;
        var dawHandle = wrap.dataset.daw;
        var wraps = getWrapsForDaw(section, dawHandle);
        var currentPos = -1;
        for (var i = 0; i < wraps.length; i++) {
          if (wraps[i] === wrap) { currentPos = i; break; }
        }
        if (currentPos >= 0 && currentPos < wraps.length - 1) {
          switchToPosition(dawHandle, currentPos + 1);
          var nextWrap = section.querySelector('.wsh-daw-action__video-wrap[data-active="true"]');
          if (nextWrap) {
            var nextVideo = nextWrap.querySelector('video');
            if (nextVideo) nextVideo.play().catch(function () {});
          }
        }
      });
    });

    // IntersectionObserver: upgrade active video preload on viewport entry
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            section.dataset.wshInView = 'true';
            var activeWrap = section.querySelector('.wsh-daw-action__video-wrap[data-active="true"]');
            if (activeWrap) {
              var video = activeWrap.querySelector('video');
              if (video && video.preload === 'none') {
                video.preload = 'metadata';
              }
            }
            observer.disconnect();
          }
        });
      }, { threshold: 0.1 });

      observer.observe(section);
    }

    // Initialize: force position 0 (first by kit_number sort) for the default DAW.
    // Liquid marks the first metafield-order entry as active, which may not be
    // the lowest kit_number. switchToPosition corrects this on init.
    var activeWrap = section.querySelector('.wsh-daw-action__video-wrap[data-active="true"]');
    if (activeWrap) {
      switchToPosition(activeWrap.dataset.daw, 0);
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on section re-render (Shopify Section Rendering API)
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector(SECTION_SEL)) {
      var s = e.target.querySelector(SECTION_SEL);
      if (s) s.dataset.wshInited = '';
      init();
    }
  });
})();
